var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { KeyIcon, PowerIcon, RectangleStackIcon, TrashIcon, UserIcon, } from "@heroicons/react/24/outline";
import { forwardRef, useContext, useEffect, useImperativeHandle, useState, } from "@wordpress/element";
import { __ } from "@wordpress/i18n";
import { CLOSE_USER_MODAL, DELETE_USER, EDIT_USER, RESET_PASSWORD, } from "../../../constants";
import { useUserActions } from "../../../hooks/actions/useUserActions";
import { useLoadingStates } from "../../../hooks/useLoadingStates";
import { AppContext } from "../../../providers/AppContextProvider";
import { ModalContext } from "../../../providers/ModalContextProvider";
import { UserContext } from "../../../providers/UserContextProvider";
import { CustomFieldEntityType } from "../../../types/custom-field";
import { WPQTIconButton } from "../../common/Button/Button";
import { WPQTInput } from "../../common/Input/Input";
import { WPQTTextarea } from "../../common/TextArea/TextArea";
import { CustomFieldsInModalWrap } from "../../CustomField/CustomFieldsInModalWrap/CustomFieldsInModalWrap";
import { UserModalTabs } from "../../Tab/CommentsAndLogs/UserModalTabs/UserModalTabs";
import { WPQTModalField, WPQTModalFieldSet, WPQTModalFooter, } from "../WPQTModal";
const UserModalContent = forwardRef(function UserModalContent({ modalSaving, editUser }, ref) {
    const { state: { userToEdit }, modalDispatch, } = useContext(ModalContext);
    const { state: { isUserAllowedToDelete }, } = useContext(AppContext);
    const [userName, setUserName] = useState("");
    const [userDescription, setUserDescription] = useState("");
    const [isActiveUser, setIsActiveUser] = useState(false);
    const [hasPassword, setHasPassword] = useState(false);
    const { changeUserStatus, deleteUser, resetUserPassword } = useUserActions();
    const { userDispatch } = useContext(UserContext);
    const { loading1: isResetPWLoading, setLoading1: setIsResetPWLoading, loading2: isActivateLoading, setLoading2: setIsActivateLoading, loading3: isDeleteLoading, setLoading3: setIsDeleteLoading, } = useLoadingStates();
    useEffect(() => {
        if (userToEdit) {
            setUserName(userToEdit.name);
            setUserDescription(userToEdit.description);
            setIsActiveUser(userToEdit.is_active);
            setHasPassword(userToEdit.has_password);
        }
    }, [userToEdit]);
    const clearContent = () => {
        setUserName("");
        setUserDescription("");
    };
    const saveUser = () => {
        if (userToEdit) {
            editUser(Object.assign(Object.assign({}, userToEdit), { name: userName, description: userDescription }));
        }
    };
    useImperativeHandle(ref, () => ({
        clearContent,
    }));
    if (!userToEdit)
        return null;
    return (_jsxs(_Fragment, { children: [_jsxs("div", { className: "wpqt-grid wpqt-grid-cols-1 wpqt-gap-7 md:wpqt-grid-cols-[1fr_auto]", children: [_jsxs("div", { className: "wpqt-border-0 wpqt-border-r wpqt-border-solid wpqt-border-r-gray-300 md:wpqt-pr-3", children: [_jsxs("div", { className: "wpqt-grid wpqt-grid-cols-1 wpqt-gap-3 md:wpqt-grid-cols-[auto_1fr]", children: [_jsx("div", { children: _jsxs(WPQTModalFieldSet, { children: [_jsx(WPQTModalField, { label: __("Name", "quicktasker"), children: _jsx(WPQTInput, { isAutoFocus: true, value: userName, onChange: (newValue) => setUserName(newValue) }) }), _jsx(WPQTModalField, { label: __("Description", "quicktasker"), children: _jsx(WPQTTextarea, { rowsCount: 3, value: userDescription, onChange: (newValue) => setUserDescription(newValue) }) })] }) }), _jsx(CustomFieldsInModalWrap, { entityId: userToEdit.id, entityType: CustomFieldEntityType.User })] }), _jsx("div", { className: "wpqt-mt-7", children: _jsx(UserModalTabs, { user: userToEdit }) })] }), _jsxs("div", { className: "wpqt-flex wpqt-flex-col wpqt-gap-2", children: [_jsx(WPQTIconButton, { icon: _jsx(UserIcon, { className: "wpqt-icon-blue wpqt-size-4" }), text: __("User details", "quicktasker"), onClick: () => {
                                    window.location.hash = `#/users/${userToEdit.id}`;
                                    modalDispatch({
                                        type: CLOSE_USER_MODAL,
                                    });
                                } }), _jsx(WPQTIconButton, { icon: _jsx(RectangleStackIcon, { className: "wpqt-icon-blue wpqt-size-5" }), text: __("User tasks", "quicktasker"), onClick: () => {
                                    window.location.hash = `#/users/${userToEdit.id}/tasks`;
                                    modalDispatch({
                                        type: CLOSE_USER_MODAL,
                                    });
                                } }), hasPassword && (_jsx(WPQTIconButton, { icon: _jsx(KeyIcon, { className: "wpqt-icon-red wpqt-size-5" }), text: __("Reset password", "quicktasker"), loading: isResetPWLoading, onClick: () => __awaiter(this, void 0, void 0, function* () {
                                    setIsResetPWLoading(true);
                                    yield resetUserPassword(userToEdit.id, () => {
                                        userDispatch({
                                            type: RESET_PASSWORD,
                                            payload: userToEdit.id,
                                        });
                                        setHasPassword(false);
                                    });
                                    setIsResetPWLoading(false);
                                }) })), !isActiveUser && (_jsx(WPQTIconButton, { icon: _jsx(PowerIcon, { className: "wpqt-icon-green wpqt-size-5" }), text: __("Activate user", "quicktasker"), loading: isActivateLoading, onClick: () => __awaiter(this, void 0, void 0, function* () {
                                    setIsActivateLoading(true);
                                    yield changeUserStatus(userToEdit.id, true, (userData) => {
                                        userDispatch({
                                            type: EDIT_USER,
                                            payload: Object.assign({}, userData),
                                        });
                                        setIsActiveUser(true);
                                    });
                                    setIsActivateLoading(false);
                                }) })), isActiveUser && (_jsx(WPQTIconButton, { icon: _jsx(PowerIcon, { className: "wpqt-icon-red wpqt-size-5" }), text: __("Disable user", "quicktasker"), loading: isActivateLoading, onClick: () => __awaiter(this, void 0, void 0, function* () {
                                    setIsActivateLoading(true);
                                    yield changeUserStatus(userToEdit.id, false, (userData) => {
                                        userDispatch({
                                            type: EDIT_USER,
                                            payload: Object.assign({}, userData),
                                        });
                                        setIsActiveUser(false);
                                    });
                                    setIsActivateLoading(false);
                                }) })), isUserAllowedToDelete && (_jsx(WPQTIconButton, { icon: _jsx(TrashIcon, { className: "wpqt-icon-red wpqt-size-5" }), text: __("Delete user", "quicktasker"), loading: isDeleteLoading, onClick: () => __awaiter(this, void 0, void 0, function* () {
                                    setIsDeleteLoading(true);
                                    yield deleteUser(userToEdit.id, (userId) => {
                                        userDispatch({
                                            type: DELETE_USER,
                                            payload: userId,
                                        });
                                        modalDispatch({
                                            type: CLOSE_USER_MODAL,
                                        });
                                    });
                                    setIsDeleteLoading(false);
                                }) }))] })] }), _jsx(WPQTModalFooter, { onSave: saveUser, saveBtnText: modalSaving
                    ? __("Saving...", "quicktasker")
                    : __("Save", "quicktasker") })] }));
});
export { UserModalContent };
